import { getApp, getApps, initializeApp, type FirebaseApp } from "firebase/app";
import {
  CustomProvider,
  initializeAppCheck,
  ReCaptchaV3Provider,
  setTokenAutoRefreshEnabled,
  type AppCheckToken,
} from "firebase/app-check";
import {
  getFunctions,
  connectFunctionsEmulator,
  type Functions,
} from "firebase/functions";
import { getAnalytics, type Analytics } from "firebase/analytics";
import {
  fetchAndActivate,
  getRemoteConfig,
  type RemoteConfig,
} from "firebase/remote-config";
import {
  defaultBucket,
  firebaseConfig,
  recaptchaKey,
} from "@/config/firebase.config";
import {
  collection,
  persistentLocalCache,
  persistentSingleTabManager,
  initializeFirestore,
  connectFirestoreEmulator,
  Firestore,
  CollectionReference,
} from "firebase/firestore";
import {
  getMessaging,
  isSupported,
  onMessage,
  type Messaging,
} from "firebase/messaging";
import {
  getStorage,
  connectStorageEmulator,
  type FirebaseStorage,
} from "firebase/storage";
import { getAuth, connectAuthEmulator, type Auth } from "firebase/auth";
import { setInfoToast } from "@/helpers/notifications/toast";
import router from "@/router/router";
import {
  hasSupportForNativeAppCheck,
  isRunningInFlutter,
} from "@/helpers/flutter/capabilities";
import { markRaw } from "vue";
import { defaultRemoteConfig } from "@/config/default-remote-config";
import { requestNativeAppCheckToken } from "../flutter/features/native-app-check";

// Initialize Firebase app
export const firebaseApp: FirebaseApp =
  getApps().length === 0 ? initializeApp(firebaseConfig) : getApp();

// Set up app check debug token in development
if (import.meta.env.VITE_APP_APP_CHECK_DEBUG_TOKEN) {
  self.FIREBASE_APPCHECK_DEBUG_TOKEN =
    import.meta.env.VITE_APP_APP_CHECK_DEBUG_TOKEN;
}

// Initialize app check
if (hasSupportForNativeAppCheck()) {
  const appCheckFlutterNativeProvider = new CustomProvider({
    getToken: requestNativeAppCheckToken,
  });

  initializeAppCheck(firebaseApp, {
    provider: appCheckFlutterNativeProvider,
    isTokenAutoRefreshEnabled: true,
  });
} else {
  // Oldschool recaptcha app check for web and old flutter
  initializeAppCheck(firebaseApp, {
    provider: new ReCaptchaV3Provider(recaptchaKey),
    isTokenAutoRefreshEnabled: true,
  });
}

// Initialize firebase methods
export const firestore: Firestore = markRaw(
  initializeFirestore(firebaseApp, {
    localCache: persistentLocalCache({
      tabManager: persistentSingleTabManager({}),
    }),
  }),
);

export const firebaseAuth: Auth = getAuth(firebaseApp);
firebaseAuth.useDeviceLanguage();

export const firebaseFunctions: Functions = getFunctions(
  firebaseApp,
  "europe-west3",
);
export const firebaseStorage: FirebaseStorage = getStorage(
  firebaseApp,
  defaultBucket,
);
export const firebaseAnalytics: Analytics = getAnalytics(firebaseApp);
export const firebaseRemoteConfig: RemoteConfig = getRemoteConfig(firebaseApp);
firebaseRemoteConfig.defaultConfig = defaultRemoteConfig;
//One hour fetch interval
firebaseRemoteConfig.settings.minimumFetchIntervalMillis = 1000 * 60 * 60;
fetchAndActivate(firebaseRemoteConfig).then().catch();

// Initialize messaging for supported browsers (async initialization)
export let firebaseMessaging: Messaging | null = null;
isSupported().then((supported) => {
  //only supported browsers
  if (supported) {
    //Never run in flutter
    if (isRunningInFlutter()) {
      return;
    }
    try {
      firebaseMessaging = getMessaging(firebaseApp);
      onMessage(firebaseMessaging, (payload) => {
        setInfoToast({
          title: payload.notification?.title,
          message: payload.notification?.body,
          timeout: 5000,
          click: () => {
            if (payload?.fcmOptions?.link) {
              router.push(payload.fcmOptions.link);
            }
          },
        });
      });
    } catch {
      //
    }
  }
});

//Emulators
if (import.meta.env.VITE_USE_EMULATORS === "true") {
  const emulatorHost = import.meta.env.VITE_EMULATOR_HOST || "127.0.0.1";
  connectAuthEmulator(firebaseAuth, `http://${emulatorHost}:9099`);
  connectFirestoreEmulator(firestore, emulatorHost, 8082);
  connectFunctionsEmulator(firebaseFunctions, emulatorHost, 5001);
  connectStorageEmulator(firebaseStorage, emulatorHost, 9199);
}

export const usersCollection: CollectionReference = collection(
  firestore,
  "users",
);
export const groupsCollection: CollectionReference = collection(
  firestore,
  "groups",
);
